import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Users, Clock, TrendingUp, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

export default function ResourceAllocation({ project, tasks, teamMembers, timeEntries }) {
  // Beregn timer per bruker
  const userStats = teamMembers.map(member => {
    const userTasks = tasks.filter(t => t.assigned_to === member.user_email);
    const userTimeEntries = timeEntries.filter(e => e.user_email === member.user_email);
    
    // Beregn registrert tid
    const hoursSpent = userTimeEntries.reduce((sum, e) => sum + (e.duration_minutes || 0) / 60, 0);
    
    // Beregn estimert gjenstående tid
    const estimatedHours = userTasks
      .filter(t => t.status !== "completed" && t.status !== "cancelled")
      .reduce((sum, task) => {
        if (!task.estimated_hours) return sum;
        const taskTimeSpent = userTimeEntries
          .filter(e => e.task_id === task.id)
          .reduce((s, e) => s + (e.duration_minutes || 0) / 60, 0);
        return sum + Math.max(0, task.estimated_hours - taskTimeSpent);
      }, 0);
    
    // Beregn kapasitetsutnyttelse (antar 40 timer per uke)
    const weeksInProject = project.end_date && project.start_date 
      ? Math.max(1, Math.ceil((new Date(project.end_date) - new Date(project.start_date)) / (7 * 24 * 60 * 60 * 1000)))
      : 1;
    const availableHours = weeksInProject * 40;
    const utilization = ((hoursSpent + estimatedHours) / availableHours) * 100;
    
    return {
      ...member,
      hoursSpent: hoursSpent.toFixed(1),
      estimatedHours: estimatedHours.toFixed(1),
      totalHours: (hoursSpent + estimatedHours).toFixed(1),
      utilization: Math.min(utilization, 100),
      isOverallocated: utilization > 100,
      activeTasks: userTasks.filter(t => t.status === "in_progress").length,
      completedTasks: userTasks.filter(t => t.status === "completed").length,
      totalTasks: userTasks.length,
    };
  });

  // Beregn totaler
  const totalHoursSpent = userStats.reduce((sum, u) => sum + parseFloat(u.hoursSpent), 0);
  const totalEstimatedHours = userStats.reduce((sum, u) => sum + parseFloat(u.estimatedHours), 0);
  const totalHours = totalHoursSpent + totalEstimatedHours;

  return (
    <Card className="p-6 bg-white border-0 shadow-sm">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-lime-100 to-lime-200 flex items-center justify-center">
          <Users className="w-5 h-5 text-zinc-900" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Ressursallokering</h3>
          <p className="text-sm text-slate-500">Teamkapasitet og tidsbruk</p>
        </div>
      </div>

      {/* Totaloversikt */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="p-4 bg-slate-50 rounded-xl">
          <p className="text-sm text-slate-500 mb-1">Totalt brukt</p>
          <p className="text-2xl font-bold text-slate-900">{totalHoursSpent.toFixed(1)}t</p>
          <p className="text-xs text-slate-500 mt-1">Registrert tid</p>
        </div>
        <div className="p-4 bg-amber-50 rounded-xl">
          <p className="text-sm text-slate-500 mb-1">Estimert gjenstående</p>
          <p className="text-2xl font-bold text-amber-600">{totalEstimatedHours.toFixed(1)}t</p>
          <p className="text-xs text-slate-500 mt-1">Basert på oppgaver</p>
        </div>
        <div className="p-4 bg-lime-100 rounded-xl">
          <p className="text-sm text-slate-500 mb-1">Total estimert</p>
          <p className="text-2xl font-bold text-zinc-900">{totalHours.toFixed(1)}t</p>
          <p className="text-xs text-slate-500 mt-1">Totalt for prosjektet</p>
        </div>
      </div>

      {/* Ressurser per bruker */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-slate-900">Teammedlemmer</h4>
        {userStats.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-4">Ingen teammedlemmer tildelt</p>
        ) : (
          userStats.map((user) => (
            <div key={user.user_email} className="p-4 bg-slate-50 rounded-xl">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-medium text-slate-900">{user.user_name}</p>
                  <p className="text-xs text-slate-500">{user.user_email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {user.activeTasks} aktive
                  </Badge>
                  {user.isOverallocated && (
                    <Badge className="bg-rose-100 text-rose-700 border-0">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Overallokert
                    </Badge>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-3">
                <div>
                  <p className="text-xs text-slate-500">Brukt</p>
                  <p className="text-lg font-semibold text-slate-900">{user.hoursSpent}t</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Gjenstående</p>
                  <p className="text-lg font-semibold text-amber-600">{user.estimatedHours}t</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Totalt</p>
                  <p className="text-lg font-semibold text-zinc-900">{user.totalHours}t</p>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">Kapasitetsutnyttelse</span>
                  <span className={cn(
                    "font-medium",
                    user.utilization > 100 ? "text-rose-600" :
                    user.utilization > 80 ? "text-amber-600" : "text-emerald-600"
                  )}>
                    {user.utilization.toFixed(0)}%
                  </span>
                </div>
                <Progress 
                  value={user.utilization} 
                  className={cn(
                    "h-2",
                    user.utilization > 100 ? "[&>div]:bg-rose-500" :
                    user.utilization > 80 ? "[&>div]:bg-amber-500" : "[&>div]:bg-emerald-500"
                  )}
                />
              </div>

              <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                <span>{user.completedTasks} av {user.totalTasks} oppgaver fullført</span>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-xs text-blue-700">
          💡 Kapasitetsutnyttelse beregnes basert på 40 timer per uke gjennom prosjektets varighet
        </p>
      </div>
    </Card>
  );
}